import { Alert, Box, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import BotonVolver from "../../components/kit/BotonVolver";
import AvatarAmpliable from "../../components/kit/AvatarAmpliable";
import Etiqueta from "../../components/kit/Etiqueta";
import { Tarjeta } from "../../components/kit/Tarjeta";
import { useDocenteFicha } from "../../queries/useDocentes";
import { tokens } from "../../theme";
import { GestoDeApertura, useIr } from "../../utils/enlaces";

/**
 * Ficha de un profesor: acá viven sus áreas.
 *
 * En la grilla de tarjetas no caben —son casi sesenta áreas finas y cada profesor
 * declara varias—, así que la tarjeta lleva acá y esta página tiene el espacio
 * para mostrarlas ordenadas por supercategoría.
 *
 * Las supercategorías son solo la forma de ordenar lo que se ve: la afinidad que
 * usa el armado de comisiones sigue calculándose sobre el área fina.
 */
export default function DocenteFicha() {
  const { rut } = useParams();
  const { data: docente, isLoading, isError } = useDocenteFicha(Number(rut));

  const ir = useIr();
  const abrirTema = (e: GestoDeApertura, id: number) => ir(e, `/app/temas/${id}`);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !docente) {
    return (
      <Box>
        <BotonVolver fallback="/app/docentes" etiquetaFallback="Docentes" />
        <Alert severity="error" sx={{ mt: 2 }}>
          No se pudo cargar la ficha de este profesor.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <BotonVolver fallback="/app/docentes" etiquetaFallback="Docentes" />

      <Tarjeta>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ xs: "center", sm: "flex-start" }}>
          <AvatarAmpliable src={docente.foto} nombre={docente.nombre} tamano={120} aumento={1.5} />
          <Box sx={{ minWidth: 0, textAlign: { xs: "center", sm: "left" } }}>
            <Typography variant="h5" sx={{ lineHeight: 1.25 }}>
              {docente.nombre_completo}
            </Typography>
            {docente.email ? (
              <Link
                href={`mailto:${docente.email}`}
                variant="body1"
                underline="hover"
                sx={{ color: tokens.marca.texto, wordBreak: "break-all" }}
              >
                {docente.email}
              </Link>
            ) : (
              <Typography variant="body1" color="text.disabled">
                Sin correo cargado
              </Typography>
            )}
          </Box>
        </Stack>
      </Tarjeta>

      <Seccion titulo="Áreas de investigación">
        {docente.areas_por_supercategoria.length ? (
          <Stack spacing={2}>
            {docente.areas_por_supercategoria.map((grupo) => (
              <Box key={grupo.id ?? "otras"}>
                <Typography variant="subtitle1" sx={{ color: tokens.marca.texto, mb: 0.75 }}>
                  {grupo.nombre}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.6}>
                  {grupo.areas.map((a) => (
                    <Etiqueta key={a.id}>{a.nombre}</Etiqueta>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Este profesor todavía no ha declarado áreas.
          </Typography>
        )}
      </Seccion>

      <Seccion titulo="Últimos temas">
        {docente.temas.length ? (
          <Stack spacing={1}>
            {docente.temas.map((t) => (
              <Link
                key={t.id}
                component="button"
                type="button"
                variant="body1"
                underline="hover"
                onClick={(e) => abrirTema(e, t.id)}
                onAuxClick={(e) => abrirTema(e, t.id)}
                sx={{ color: tokens.marca.texto, textAlign: "left" }}
              >
                {t.titulo}
              </Link>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Todavía no ha publicado temas.
          </Typography>
        )}
      </Seccion>
    </Box>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {titulo}
      </Typography>
      {children}
    </Box>
  );
}
